import { useCallback, useEffect, useMemo, useRef, type FC } from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import { getPageviewData } from '../../utils/tracking/searchBrowse/getPageviewData.ts';
import { getPublisherTagInstance } from 'dibs-google-ad-manager/exports/getPublisherTagInstance.ts';
import { trackSortEvent } from '../../utils/tracking/searchBrowse/trackSortEvent.ts';
import { addPersonalizationTrackingData } from '../../utils/tracking/searchBrowse/addPersonalizationTrackingData.ts';
import { trackNullResultsDisplayed } from '../../utils/tracking/searchBrowse/nullResultsTracking.ts';
import { useSharedTracking } from '../../shared/SharedTrackingContainer.tsx';
import { pageTypeConstants } from '../../constants/pageTypeConstants.ts';
import { SEARCH_TYPES, FILTER_SEARCH_TYPE } from '../../constants/sbConstants.ts';
import { resetGtmTracking } from 'dibs-tracking';
import { getFilterValueByUrlLabel } from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers.ts';
import {
    itemPriceInSellabilityTracking,
    lowerPriceResultsTracking,
    trackActiveAbTests,
    trackUrgencySignals,
    trackShippingCostSb,
    trackContentVisibilitySb,
    trackShipsFromSBTile,
    trackRemoveCounts,
    trackTotalSponsoredItems,
    trackVectorSearchFallback,
} from '../SbSharedTrackingFunctions/SbSharedTrackingFunctions.ts';
import { trackQuickFiltersRedesignTest } from '../../utils/abTestHelper.ts';
import { updateSaveSearchMutation } from '../../mutations/updateSaveSearchMutation.ts';
import { trackSeoTests } from '../../pdp/helpers/trackSeoTests.ts';
import { type RefineMenuFiltersMap } from '../SbSharedRefineMenu/sbSharedRefineMenuConstants.ts';
import { parsePageType } from '../../components/global/SaveSearchButton/SaveSearchButton.tsx';

import { useSbSelector } from '../../reducers/useSbSelector.ts';

import { isMultiVerticalPage } from '../../shared/helpers/multiVerticalPageHelpers.ts';

import { type useSbSharedPageTracking_itemSearch$key } from './__generated__/useSbSharedPageTracking_itemSearch.graphql.ts';
import { type useSbSharedPageTracking_user$key } from './__generated__/useSbSharedPageTracking_user.graphql.ts';

const { SEARCH } = pageTypeConstants;
const { FUZZY } = SEARCH_TYPES;

function parseSavedSearch({
    pageType,
    isFollowing,
}: {
    pageType: string | null;
    isFollowing: boolean;
}): string | null {
    if (!isFollowing) {
        return null;
    }

    const parsedPageType = parsePageType(pageType);

    if (parsedPageType === pageTypeConstants.DEALER) {
        return 'saved seller';
    } else if (parsedPageType === pageTypeConstants.CREATOR) {
        return 'saved creator';
    } else {
        return 'saved search';
    }
}

const itemSearchFragment = graphql`
    fragment useSbSharedPageTracking_itemSearch on ItemSearchQueryConnection {
        topCategoryL1
        pageType
        searchEntities
        searchTerm
        totalResults
        displayUriRef
        browseContentModule(contentModuleId: $contentModuleId) {
            __typename
        }
        appliedFilters {
            name
            values {
                displayName
                urlLabel
                code
            }
        }
        sort {
            currentOption {
                urlLabel
            }
        }
        categoryTiles {
            __typename
        }
        searchCorrections {
            searchType
        }
        seoAbTestV2List {
            ...trackSeoTests_abTestConfigV2UserVariant
        }
        searchResultData {
            bm25ResultCount
            vectorResultCount
        }
        ...SbSharedTrackingFunctions_itemSearch
        ...areFiltersApplied_itemSearch
    }
`;
const userFragment = graphql`
    fragment useSbSharedPageTracking_user on User {
        serviceId
        isVerifiedTrade
        favorites(first: 1, types: $followSearchTypes, pages: $followSearchPages) {
            edges {
                node {
                    serviceId
                }
            }
        }
        ...SharedTrackingContainer_user
        ...sellerBrandingHelpers_user
    }
`;

type Props = {
    itemSearch: useSbSharedPageTracking_itemSearch$key;
    user: useSbSharedPageTracking_user$key | null;
    reinitTracking?: () => void;
    refineMenuFiltersMap: RefineMenuFiltersMap;
    searchCorrectionTerm?: string | null;
};

export const useSbSharedPageTracking: FC<Props> = ({
    itemSearch: itemSearchRef,
    user: userRef,
    reinitTracking = () => {},
    refineMenuFiltersMap,
    searchCorrectionTerm = null,
}) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const user = useFragment(userFragment, userRef);
    const isFollowing = !!user?.favorites?.edges?.[0]?.node?.serviceId;

    const generatedUriRef = useSbSelector(state => state.filters.generatedUriRef);
    const environment = useRelayEnvironment();
    const pageNumber = useSbSelector(state => state.filters.page);

    const propsRef = useRef<{ generatedUriRef: string | null; sortValue: string | null }>({
        generatedUriRef: null,
        sortValue: null,
    });

    const {
        appliedFilters,
        browseContentModule,
        categoryTiles,
        displayUriRef,
        pageType,
        searchCorrections,
        sort,
        topCategoryL1,
        totalResults,
        searchEntities,
        searchTerm,
    } = itemSearch;
    const searchType = searchCorrections?.searchType;
    const sortValue = sort?.currentOption?.urlLabel || null;
    const userId = user?.serviceId || null;
    const isModule = !!browseContentModule;
    const categoryTilesExist = categoryTiles?.length ? 'y' : 'n';
    const categoryTilesVisible = !isModule && !user?.isVerifiedTrade ? categoryTilesExist : null;
    const isFuzzyResults = !!getFilterValueByUrlLabel(appliedFilters, FILTER_SEARCH_TYPE, FUZZY);
    const shouldTrackNullResultDisplay = totalResults === 0 || isFuzzyResults;

    const trackUserSearchHistory = useCallback(() => {
        const hasResults = (totalResults || 0) > 0;
        const isSearch = pageType === SEARCH;

        if (
            isSearch &&
            userId &&
            hasResults &&
            !isMultiVerticalPage({ pageType, itemSearch, searchTerm })
        ) {
            updateSaveSearchMutation(environment, {
                favoriteId: null,
                page: displayUriRef || '',
                type: 'SEARCH_HISTORY',
                userId,
            });
        }
    }, [totalResults, pageType, userId, itemSearch, searchTerm, environment, displayUriRef]);

    const trackOnPageChange = useCallback(() => {
        if (sortValue) {
            if (propsRef.current.sortValue && propsRef.current.sortValue !== sortValue) {
                trackSortEvent(sortValue);
            }
            // Setting hook state values after tracking functions, so they won't be called on initial load
            propsRef.current.sortValue = sortValue;
        }
    }, [sortValue]);

    const handleTrackingFired = useCallback(() => {
        if (shouldTrackNullResultDisplay) {
            trackNullResultsDisplayed();
        }

        addPersonalizationTrackingData({
            appliedFilters,
            environment,
        });
        trackUserSearchHistory();
        itemPriceInSellabilityTracking({ pageType, sortValue });
        lowerPriceResultsTracking({ pageType, sortValue });
        trackUrgencySignals();
        trackShippingCostSb();
        trackContentVisibilitySb();
        trackShipsFromSBTile();
        trackRemoveCounts();
        trackQuickFiltersRedesignTest();
        trackActiveAbTests(itemSearch);
        trackSeoTests({ seoAbTestV2List: itemSearch.seoAbTestV2List });
        trackTotalSponsoredItems(itemSearch, pageNumber);
        trackVectorSearchFallback(itemSearch);

        // This function should contain tracking events that are fired only, if user change something manually (eg sort)
        trackOnPageChange();
    }, [
        appliedFilters,
        environment,
        itemSearch,
        pageType,
        shouldTrackNullResultDisplay,
        sortValue,
        trackOnPageChange,
        trackUserSearchHistory,
        pageNumber,
    ]);

    const { bm25ResultCount, vectorResultCount } = itemSearch.searchResultData || {};

    const gtmPageInfo = useMemo(() => {
        const additional = {
            ...getPageviewData({
                searchCorrectionTerm,
                pageType,
                appliedFilters,
                totalResults: totalResults || 0,
                pageNumber,
                optPageURL: displayUriRef,
                sortValue,
                refineMenuFiltersMap,
                isModule,
                topCategoryL1,
                searchType: searchType || null,
                searchEntities,
                savedSearch: parseSavedSearch({ pageType, isFollowing }),
                bm25ResultCount,
                vectorResultCount,
            }),
            // track categoryTiles when they are visible and for non-trade users only
            ...(categoryTilesVisible ? { categoryTilesVisible } : null),
        };

        return { additional };
    }, [
        appliedFilters,
        categoryTilesVisible,
        displayUriRef,
        isModule,
        pageNumber,
        pageType,
        refineMenuFiltersMap,
        searchCorrectionTerm,
        searchEntities,
        searchType,
        sortValue,
        topCategoryL1,
        totalResults,
        isFollowing,
        bm25ResultCount,
        vectorResultCount,
    ]);

    // `generatedUriRef` is updated in redux store before new items' refetch is finished (eg. page=2), so we can reset gtm tracking now
    useEffect(() => {
        if (generatedUriRef) {
            // Skipping initial page load
            if (
                propsRef.current.generatedUriRef &&
                propsRef.current.generatedUriRef !== generatedUriRef
            ) {
                resetGtmTracking();

                // FINDING-13256 reset tracking fired in DBL when URL updates w/o hard refresh
                reinitTracking();

                const gt = getPublisherTagInstance();
                if (gt) {
                    gt.pubads().refresh();
                }
            }
            // Saving initial truthy generatedUriRef value, so we could track if generatedUriRef has changed
            propsRef.current.generatedUriRef = generatedUriRef;
        }
    }, [generatedUriRef, reinitTracking]);

    useSharedTracking({
        user,
        gtmPageInfo,
        onTrackingFired: handleTrackingFired,
    });

    return null;
};
